import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { IPatientPurchase } from '../../../library/models/patientPurchase';
import { useApiFormError } from '../../../library/useApiFormError';
import { useIsCompact } from '../../../library/useIsCompact';
import { numericSlotProps } from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import {
    AdjustSessionsUsedBeforeFormValues,
    createAdjustSessionsUsedBeforeSchema,
    sessionsUsedBeforeBounds,
} from './adjustSessionsUsedBeforeSchema';
import {
    descriptionStyles,
    dialogActionsStyles,
    dialogTitleStyles,
    formAlertStyles,
} from './adjustSessionsUsedBeforeDialog.styles';

interface AdjustSessionsUsedBeforeDialogProps {
    open: boolean;
    purchase: IPatientPurchase;
    onClose: () => void;
    /** Called once the server has the new count, so the card can refresh its own. */
    onSaved: () => void;
}

/**
 * For an administrator bringing a patient across from the clinic's old system halfway
 * through a package: how many of its sessions were given before Carefull.
 *
 * Only that one count moves. What was sold, what it cost and the diary are left alone, and
 * saving 0 puts the purchase back as it was sold.
 */
export const AdjustSessionsUsedBeforeDialog = (
    props: AdjustSessionsUsedBeforeDialogProps
): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();
    const { purchase } = props;

    const bounds = useMemo(() => sessionsUsedBeforeBounds(purchase), [purchase]);
    const schema = useMemo(
        () => createAdjustSessionsUsedBeforeSchema(translate, bounds),
        [translate, bounds]
    );

    const {
        register, handleSubmit, reset, setError, formState: { errors, isValid },
    } = useValidatedForm<AdjustSessionsUsedBeforeFormValues>(schema, {
        defaultValues: { sessionsUsedBefore: '' },
    });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<AdjustSessionsUsedBeforeFormValues>(
        setError, knownFields, translate, ResourcesIds.PURCHASE_USED_BEFORE_ERROR);

    const save = useReactMutation(
        services.patientPurchases.setSessionsUsedBeforeMutation(purchase.id)
    );

    // Opens on what is stored now, never on what was typed the last time.
    useEffect(() => {
        if (props.open) {
            reset({ sessionsUsedBefore: String(purchase.sessionsUsedBefore) });
            clear();
        }
    }, [props.open, purchase.sessionsUsedBefore, reset, clear]);

    const onValid = (values: AdjustSessionsUsedBeforeFormValues): void => {
        clear();
        save.mutate(
            { sessionsUsedBefore: Number(values.sessionsUsedBefore) },
            { onSuccess: props.onSaved, onError: report }
        );
    };

    return (
        <Dialog
            open={props.open}
            onClose={save.isPending ? undefined : props.onClose}
            maxWidth="xs"
            fullWidth
            fullScreen={isCompact}
        >
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.PURCHASE_USED_BEFORE_TITLE)}
            </DialogTitle>

            <DialogContent>
                {formError && <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>}

                <Typography variant="body2" sx={descriptionStyles}>
                    {translate(ResourcesIds.PURCHASE_USED_BEFORE_DESCRIPTION, {
                        total: purchase.totalSessions,
                    })}
                </Typography>

                <TextField
                    {...register('sessionsUsedBefore')}
                    type="number"
                    fullWidth
                    label={translate(ResourcesIds.PURCHASE_USED_BEFORE_LABEL)}
                    slotProps={numericSlotProps(bounds)}
                    {...fieldProps(
                        errors,
                        'sessionsUsedBefore',
                        translate(ResourcesIds.PURCHASE_USED_BEFORE_HINT, { ...bounds })
                    )}
                />
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
                <Button onClick={props.onClose} variant="outlined" disabled={save.isPending}>
                    {translate(ResourcesIds.CANCEL_TEXT)}
                </Button>
                <Button
                    onClick={handleSubmit(onValid)}
                    variant="contained"
                    disabled={!isValid || save.isPending}
                >
                    {translate(ResourcesIds.SAVE_CHANGES)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
